import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { z } from 'zod'
import { commandPayload, commands, isLiveRuntime } from '../../lib/runtime'
import { subscribePrototypeRuntime } from '../../stores/activeRuntime'
import { isEarnedThisWeek } from './chefDashboard'

/**
 * A chef's own earnings and payouts, read from the payout ledger
 * (`billing:list_my_payouts`).
 *
 * This is the only authoritative record of chef money. The chef wallet rows
 * (`wallet_transactions`, owner_class 'chef') are written on payment but never
 * settled, so a screen built on them showed a balance and a Cashout button for
 * money that was in no wallet.
 */

const minor = z.coerce.number().int().safe()

const earningSchema = z.object({
  order_id: z.string(),
  amount_minor: minor,
  state: z.enum(['sent', 'held', 'releasing', 'paid_out', 'reversed']),
  created_at: z.string(),
})

const releaseSchema = z.object({
  id: z.string(),
  amount_minor: minor,
  method: z.string(),
  status: z.string(),
  created_at: z.string(),
  completed_at: z.string().default(''),
})

const payoutsSchema = z.object({
  sent_minor: minor,
  held_minor: minor,
  releasing_minor: minor,
  paid_out_minor: minor,
  earnings: z.array(earningSchema),
  releases: z.array(releaseSchema),
})

export type ChefEarning = z.infer<typeof earningSchema>
export type ChefPayoutRelease = z.infer<typeof releaseSchema>
export type ChefPayouts = z.infer<typeof payoutsSchema>

export const EMPTY_PAYOUTS: ChefPayouts = {
  sent_minor: 0, held_minor: 0, releasing_minor: 0, paid_out_minor: 0, earnings: [], releases: [],
}

/** Everything the chef has earned, wherever it currently sits. */
export const totalEarnedMinor = (p: ChefPayouts) =>
  p.sent_minor + p.held_minor + p.releasing_minor + p.paid_out_minor

/** Earnings from orders paid this week (Monday onwards), excluding reversals. */
export const weekEarnedMinor = (p: ChefPayouts) =>
  p.earnings
    .filter((e) => e.state !== 'reversed' && isEarnedThisWeek(e.created_at))
    .reduce((sum, e) => sum + e.amount_minor, 0)

export const earningStateLabel: Record<ChefEarning['state'], string> = {
  sent: 'Sent to your bank by Paystack',
  held: 'Held by ChooseChow, paid out once your bank account is active',
  releasing: 'On its way to your bank',
  paid_out: 'Paid to your bank',
  reversed: 'Refunded to the customer',
}

export const releaseStatusLabel = (status: string): string =>
  status === 'paid' ? 'Paid to your bank'
    : status === 'processing' ? 'On its way'
      : status === 'reversed' ? 'Returned by the bank, will be resent'
        : 'Delayed, ChooseChow will retry'

export const useChefPayouts = () => {
  const live = useSyncExternalStore(subscribePrototypeRuntime, isLiveRuntime, () => false)
  const [data, setData] = useState<ChefPayouts>(EMPTY_PAYOUTS)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const version = useRef(0)

  const load = useCallback(async () => {
    const current = ++version.current
    // The prototype has no payout ledger; it shows the empty state.
    if (!live) {
      setData(EMPTY_PAYOUTS)
      setLoading(false)
      return
    }
    setLoading(true)
    setError('')
    try {
      const parsed = payoutsSchema.parse(commandPayload(await commands.billing.listMyPayouts()))
      if (current === version.current) setData(parsed)
    } catch {
      if (current === version.current) setError('Your earnings could not be loaded. Pull to refresh or try again.')
    } finally {
      if (current === version.current) setLoading(false)
    }
  }, [live])

  useEffect(() => {
    void load()
  }, [load])

  return { data, loading, error, reload: load }
}
